/* eslint-disable import/no-anonymous-default-export */
import type { Theme } from '@mui/material';
import {
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import { formatDate } from '@/utils/date_utils';
import {
    RecordContextProvider,
    SimpleListLoading,
    useListContext,
} from 'react-admin';
import { Link } from 'react-router-dom';
import {
    defaultQuoteStatuses,
    defaultTypeOfWork,
    quoteStatuses,
} from '@/root/defaultConfiguration';

import { Status } from '@/components/misc/Status';
import { QuoteListItem } from '@/types';

const pendingFinishColor =
    defaultQuoteStatuses.find(s => s.value === '3')?.color ?? '#f0ad4e';

function estimateSentEntry(item: QuoteListItem) {
    return (item.informative_status || []).find(s => s.status === 'is_estimate_sent');
}

function QuoteStatusFlags({ item }: { item: QuoteListItem }) {
    const sent = estimateSentEntry(item);
    return (
        <>
            {sent?.date ? (
                <Tooltip
                    title={`Estimate sent ${formatDate(sent.date)}`}
                    placement="top"
                >
                    <FlagIcon
                        sx={{
                            color: 'info.main',
                            fontSize: 18,
                            verticalAlign: 'middle',
                        }}
                        aria-label={`Estimate sent ${formatDate(sent.date)}`}
                    />
                </Tooltip>
            ) : null}
            {item.status === quoteStatuses.approved ? (
                <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: 'success.main', fontWeight: 600 }}
                >
                    Approved
                </Typography>
            ) : item.status === quoteStatuses.pending_finish_quoting ? (
                <Tooltip title="Pending finish quoting" placement="top">
                    <FlagIcon
                        sx={{
                            color: pendingFinishColor,
                            fontSize: 18,
                            verticalAlign: 'middle',
                        }}
                        aria-label="Pending finish quoting"
                    />
                </Tooltip>
            ) : (
                <Status status={item.status} />
            )}
        </>
    );
}

export const QuoteListContent = ({
    linkPrefix = '/quotes',
    selectable = true,
    showProjectSummary = true,
}: {
    linkPrefix?: string;
    selectable?: boolean;
    showProjectSummary?: boolean;
}) => {
    const {
        data: quotes,
        error,
        isPending,
        onToggleItem,
        selectedIds,
    } = useListContext<QuoteListItem>();
    const isSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('md'),
    );

    if (isPending) {
        return <SimpleListLoading hasLeftAvatarOrIcon hasSecondaryText />;
    }
    if (error) {
        return null;
    }

    return (
        <>
            <List dense>
                {quotes.map(item => {
                    const typeLabel =
                        item.type_of_work in defaultTypeOfWork
                            ? defaultTypeOfWork[item.type_of_work]
                            : item.type_of_work;
                    const metaSecondary = item.creator_name
                        ? `${item.creator_name}${item.city ? ` — ${item.city}` : ''}`
                        : item.city;

                    return (
                        <RecordContextProvider key={item.id} value={item}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    component={Link}
                                    to={`${linkPrefix}/${item.id}/show`}
                                    sx={{
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: selectable ? '2.5em' : 0,
                                            width: selectable ? undefined : 0,
                                            mt: 0.5,
                                        }}
                                    >
                                        {selectable && (
                                            <Checkbox
                                                edge="start"
                                                checked={selectedIds.includes(item.id)}
                                                tabIndex={-1}
                                                disableRipple
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onToggleItem(item.id);
                                                }}
                                            />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                component="span"
                                                variant="body1"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.75,
                                                    flexWrap: 'wrap',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {item.description || `Quote #${item.id}`}
                                                {isSmall && <QuoteStatusFlags item={item} />}
                                            </Typography>
                                        }
                                        secondary={
                                            showProjectSummary ? (
                                                <>
                                                    {item.project_name} — {item.project_address}
                                                    {!isSmall && item.creator_name ? (
                                                        <>
                                                            <br />
                                                            {item.creator_name}
                                                        </>
                                                    ) : null}
                                                    {isSmall && typeLabel
                                                        ? ` · ${typeLabel}`
                                                        : null}
                                                    {isSmall && metaSecondary
                                                        ? ` · ${metaSecondary}`
                                                        : null}
                                                    {isSmall && item.created_at
                                                        ? ` · ${formatDate(item.created_at, 'MM/dd/yyyy')}`
                                                        : null}
                                                </>
                                            ) : isSmall ? (
                                                <>
                                                    {[typeLabel, metaSecondary]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                    {item.created_at
                                                        ? `${typeLabel || metaSecondary ? ' · ' : ''}${formatDate(item.created_at, 'MM/dd/yyyy')}`
                                                        : null}
                                                </>
                                            ) : (
                                                'Created by: ' + item.creator_name || undefined
                                            )
                                        }
                                        sx={{ minWidth: 0, flex: 1 }}
                                    />
                                    {!isSmall && (
                                        <ListItemText
                                            primary={typeLabel}
                                            secondary={item.city || undefined}
                                            sx={{
                                                textAlign: 'right',
                                                maxWidth: 220,
                                                flex: '0 0 auto',
                                            }}
                                        />
                                    )}
                                    {!isSmall && item.created_at && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            title={item.created_at}
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                flex: '0 0 auto',
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-end',
                                                    lineHeight: 1.25,
                                                }}
                                            >
                                                <span>
                                                    {formatDate(item.created_at, 'MM/dd/yyyy')}
                                                </span>
                                                <span>
                                                    {formatDate(item.created_at, 'HH:mm')}
                                                </span>
                                            </Typography>
                                            <QuoteStatusFlags item={item} />
                                        </Typography>
                                    )}
                                </ListItemButton>
                            </ListItem>
                        </RecordContextProvider>
                    );
                })}

                {quotes.length === 0 && (
                    <ListItem>
                        <ListItemText primary="No quotes found" />
                    </ListItem>
                )}
            </List>
        </>
    );
};
